import {
  GetGeocodingSuggerimentiParams,
  getGeocodingCoordinateIndirizzo,
  useGetGeocodingSuggerimenti,
} from '@framework/api-client';
import { SelectOption } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { Select, message } from 'antd';
import { isEmpty } from 'lodash';
import { Map } from 'ol';
import { Extent } from 'ol/extent';
import { transform } from 'ol/proj';
import * as React from 'react';
import { useCallback, useId, useState } from 'react';

export interface IMapSearchAddressControlProps {
  map: React.RefObject<Map>;

  isMobile?: boolean | null;
}

export function MapSearchAddressControl(props: IMapSearchAddressControlProps) {
  const { map, isMobile } = props;
  const { t } = useTranslation();

  const [query, setQuery] = useState<GetGeocodingSuggerimentiParams>({
    addressToNormalize: '',
  });

  const { data, isLoading, isFetching } = useGetGeocodingSuggerimenti(query, {
    query: {
      keepPreviousData: true,
      enabled:
        !isEmpty(query.addressToNormalize) &&
        query.addressToNormalize.length > 2,
    },
  });

  const handleSearch = useCallback(
    (value: string) => {
      setQuery({ ...query, addressToNormalize: value });
    },
    [query, setQuery],
  );

  const handleSelect = useCallback(
    (value: any, option: SelectOption | SelectOption[]) => {
      async function loadAddress() {
        const magicKey = value;
        const address = await getGeocodingCoordinateIndirizzo({ magicKey });
        console.log(`Candidates: `, address);
        let addressExtent = address?.candidates?.[0]?.extent;
        console.log(`Extent: `, addressExtent);

        // Correggiamo l'extent se è fornito come array
        if (Array.isArray(addressExtent) && addressExtent.length === 4) {
          addressExtent = {
            xmin: addressExtent[0],
            ymin: addressExtent[1],
            xmax: addressExtent[2],
            ymax: addressExtent[3],
          };
        }

        if (!addressExtent) {
          message.error(
            t(
              'map-widget.search-address.no-result-found-no-extent-mpe-0001',
              "Nessun risultato trovato per l'indirizzo selezionato. (MPE-0001)",
            ),
          );
          return;
        }
        const projectedMin = transform(
          [addressExtent.xmin, addressExtent.ymin],
          `EPSG:${address.spatialReference?.wkid?.toString() ?? '3857'}`,
          'EPSG:3857',
        );

        const projectedMax = transform(
          [addressExtent.xmax, addressExtent.ymax],
          `EPSG:${address.spatialReference?.wkid?.toString() ?? '3857'}`,
          'EPSG:3857',
        );

        const normalizedExtent: Extent = [
          projectedMin[0],
          projectedMin[1],
          projectedMax[0],
          projectedMax[1],
        ];

        console.log(`Normalized extent: `, normalizedExtent);
        map.current?.getView().fit(normalizedExtent, {
          duration: 200,
        });

        setQuery({ ...query, addressToNormalize: '' });
      }

      loadAddress().catch(err => {
        console.error(err);
        message.error(
          t(
            'map-widget.search-address.load-address-error-mpe-002',
            "Errore durante il caricamento dell'indirizzo (MPE-002)",
          ),
        );
      });
    },
    [t],
  );

  const searchId = useId();

  const notFoundText = isFetching
    ? t('map-widget.search-address.not-found-text.loading', 'Caricamento...')
    : query?.addressToNormalize.length > 3
    ? t(
        'map-widget.search-address.not-found-text.no-result',
        'Nessun risultato',
      )
    : t(
        'map-widget.search-address.not-found-text.write-to-search',
        'Scrivi per cercare',
      );
  return (
    <div>
      <label htmlFor={searchId} className="sr-only">
        {t('map-widget.search-address.aria-label', 'Cerca indirizzo')}
      </label>
      <Select
        id={searchId}
        value={null}
        style={{ width: isMobile ? 140 : 180 }}
        notFoundContent={notFoundText}
        placeholder={t(
          'map-widget.search-address.placeholder',
          'Cerca indirizzo...',
        )}
        showSearch
        onSearch={handleSearch}
        loading={isFetching || isLoading}
        popupMatchSelectWidth={false}
        filterOption={false}
        autoClearSearchValue
        onChange={handleSelect}
        options={data?.suggestions?.map(suggestion => ({
          label: suggestion.normalizedAddress!,
          value: suggestion.magicKey!,
        }))}
      />
    </div>
  );
}
